/**
 * Impact stats layout variants (NOS-152). `cards` remains ImpactStatsSection
 * (untouched default). Variants share the block's content fields — switching
 * never loses content — and are token-driven via the --nos-* variables.
 *
 * Note: per-stat `_styles` is a cards-layout feature; variants define their own
 * composition and intentionally ignore it (same convention as hero variants).
 */
import { useEffect, useRef } from "react"
import type { BlockComponentProps } from "./shared"

interface Stat {
  label: string
  value: string
}

/** Mirror ImpactStatsSection's fallback: fewer than 3 stats → placeholder set. */
const DEFAULT_STATS: Stat[] = [
  { label: "Total Raised", value: "$10,250" },
  { label: "Supporters", value: "127" },
  { label: "Years Active", value: "2" },
]

function parseStatsContent(content: Record<string, unknown> | null) {
  const heading = (content?.heading as string) ?? "Our impact"
  const raw = (content?.stats as Array<{ label?: string; value?: string }>) ?? []
  const stats: Stat[] =
    raw.length >= 3
      ? raw.map((s) => ({ label: s.label ?? "Label", value: s.value ?? "0" }))
      : DEFAULT_STATS
  return { heading, stats }
}

/** Inline band: full-width primary band, one horizontal row of bare numbers. */
export function ImpactStatsInlineBand({ content }: BlockComponentProps) {
  const { heading, stats } = parseStatsContent(content)
  return (
    <section
      className="px-4 @sm:px-6 @md:px-8"
      style={{
        background: "var(--nos-color-primary, #3b82f6)",
        color: "var(--nos-color-text-on-primary, #ffffff)",
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 0.75)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 0.75)",
      }}
    >
      <div className="mx-auto max-w-5xl">
        <p className="text-sm font-semibold uppercase tracking-widest opacity-80">{heading}</p>
        <div className="mt-6 flex flex-wrap gap-x-12 gap-y-8 @md:gap-x-16">
          {stats.map((stat, i) => (
            <div key={i}>
              <p className="text-4xl font-bold @sm:text-5xl">{stat.value}</p>
              <p className="mt-1 text-sm opacity-80">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/** Oversized: left-aligned editorial stack of huge numerals, hairline separators. */
export function ImpactStatsOversized({ content }: BlockComponentProps) {
  const { heading, stats } = parseStatsContent(content)
  return (
    <section
      className="px-4 @sm:px-6 @md:px-8"
      style={{
        background: "var(--nos-color-surface, #ffffff)",
        paddingTop: "var(--nos-space-section-y, 64px)",
        paddingBottom: "var(--nos-space-section-y, 64px)",
      }}
    >
      <div className="mx-auto max-w-3xl">
        <h2
          className="text-3xl font-bold tracking-tight @sm:text-4xl"
          style={{ color: "var(--nos-color-text, #0f172a)" }}
        >
          {heading}
        </h2>
        <div className="mt-8">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="flex flex-col gap-1 py-6 @sm:flex-row @sm:items-baseline @sm:gap-6 @md:py-8"
              style={{ borderTop: "1px solid var(--nos-color-surface-alt, #f1f5f9)" }}
            >
              <p
                className="text-6xl font-bold tracking-tight @md:text-7xl"
                style={{ color: "var(--nos-color-primary, #3b82f6)" }}
              >
                {stat.value}
              </p>
              <p className="text-lg" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * NOS-162: count-up band (research §C.2 — charity:water / Pencils of Promise
 * pattern). Final values render in the initial HTML (SSR/no-JS safe — never a
 * literal "0"); an IntersectionObserver runs the count-up once on first view,
 * skipped entirely under prefers-reduced-motion.
 */
export function parseStatNumber(value: string): { prefix: string; num: number; suffix: string; decimals: number } | null {
  const m = value.match(/^([^0-9]*)([\d,]+(?:\.\d+)?)(.*)$/)
  if (!m) return null
  const num = Number.parseFloat(m[2].replace(/,/g, ""))
  if (!Number.isFinite(num)) return null
  const decimals = m[2].includes(".") ? (m[2].split(".")[1] ?? "").length : 0
  return { prefix: m[1], num, suffix: m[3], decimals }
}

function formatStatNumber(n: number, decimals: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function CountUpValue({ value }: { value: string }) {
  const ref = useRef<HTMLParagraphElement>(null)
  const ran = useRef(false)

  useEffect(() => {
    const el = ref.current
    const parsed = parseStatNumber(value)
    if (!el || !parsed || parsed.num === 0) return
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (typeof IntersectionObserver === "undefined") return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || ran.current) return
        ran.current = true
        observer.disconnect()
        const DURATION = 1400
        const start = performance.now()
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / DURATION)
          const eased = 1 - Math.pow(1 - t, 3)
          el.textContent = `${parsed.prefix}${formatStatNumber(parsed.num * eased, parsed.decimals)}${parsed.suffix}`
          if (t < 1) requestAnimationFrame(tick)
          else el.textContent = value
        }
        requestAnimationFrame(tick)
      },
      { threshold: 0.4 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [value])

  // Final value in the initial markup — count-up is enhancement only.
  return (
    <p ref={ref} className="text-5xl font-bold tracking-tight @sm:text-6xl">
      {value}
    </p>
  )
}

/** Count-up: centered stat row that animates to the final numbers on scroll-into-view. */
export function ImpactStatsCountUp({ content }: BlockComponentProps) {
  const { heading, stats } = parseStatsContent(content)
  return (
    <section
      className="px-4 text-center @sm:px-6 @md:px-8"
      style={{
        paddingTop: "var(--nos-space-section-y, 64px)",
        paddingBottom: "var(--nos-space-section-y, 64px)",
      }}
    >
      <div className="mx-auto max-w-5xl">
        <h2 className="text-2xl font-bold tracking-tight @sm:text-3xl">{heading}</h2>
        <div className="mt-10 grid grid-cols-1 gap-10 @sm:grid-cols-3">
          {stats.map((stat, i) => (
            <div key={i}>
              <CountUpValue value={stat.value} />
              <p className="mt-2 text-sm font-medium" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
